import { useEffect, useState, useCallback } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import { ClipboardText, Clock, WarningCircle, ArrowsClockwise, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

function hoursBetween(inIso, outIso) {
    if (!outIso) return null;
    return Math.round(((new Date(outIso) - new Date(inIso)) / 3600000) * 100) / 100;
}

const APPROVAL_CFG = {
    approved: { label: "Approved", chip: "chip-green" },
    pending: { label: "Pending review", chip: "chip-orange" },
    rejected: { label: "Rejected", chip: "chip-gray" },
};

export default function SiteRegister() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await endpoints.clockEvents(projectId || undefined);
            setEvents(data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { endpoints.projects().then((r) => setProjects(r.data || [])).catch(() => {}); }, []);
    useEffect(() => { load(); }, [load]);

    async function decide(eventId, status) {
        setActing(eventId);
        try { await endpoints.approveClockEvent(eventId, { status }); await load(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setActing(null); }
    }

    const active = events.filter((e) => e.status === "open");
    const closed = events.filter((e) => e.status === "closed");
    const pendingSubCount = closed.filter((e) => e.person_type === "subcontractor" && e.approval_status === "pending").length;

    return (
        <div className="max-w-[1300px] mx-auto px-6 py-8 space-y-6" data-testid="site-register-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <ClipboardText size={28} weight="bold" className="text-[var(--rom-orange)]" /> Site Register
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Who's on site right now, and every QR clock-in/out event across your projects. Subcontractor hours need approval before they're allocated to project cost.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select data-testid="register-project-filter" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm bg-white">
                        <option value="">All projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button onClick={load} className="p-2 text-[var(--rom-text-3)] hover:text-[var(--rom-text)]"><ArrowsClockwise size={15} className={loading ? "animate-spin" : ""} /></button>
                </div>
            </header>

            <div className="rom-card">
                <div className="rom-card-head"><h3 className="flex items-center gap-2"><Clock size={16} weight="bold" className="text-[var(--rom-green)]" /> Currently on site</h3><span className="chip chip-green">{active.length} active</span></div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Person</th><th>Type</th><th>Project</th><th>Clocked in</th><th className="numeric">Rate</th></tr></thead>
                        <tbody>
                            {active.length === 0 ? (
                                <tr><td colSpan={5} className="text-center py-8 text-[var(--rom-text-3)]" data-testid="register-active-empty">No one is currently clocked in.</td></tr>
                            ) : active.map((e) => (
                                <tr key={e.id} data-testid={`active-event-${e.id}`}>
                                    <td className="font-semibold">{e.person_name}{e.company_name && <span className="text-[var(--rom-text-3)] text-xs"> · {e.company_name}</span>}</td>
                                    <td><span className={`chip ${e.person_type === "employee" ? "chip-navy" : "chip-orange"}`}>{e.person_type}</span></td>
                                    <td className="text-[var(--rom-text-2)] text-xs">{e.project_name}</td>
                                    <td className="font-mono text-xs">{new Date(e.clock_in).toLocaleString()}</td>
                                    <td className="numeric font-mono">{formatCurrency(e.rate_applied)}/hr {e.rate_missing && <WarningCircle size={12} weight="bold" className="inline text-amber-600 ml-1" />}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="rom-card">
                <div className="rom-card-head">
                    <h3>Event history</h3>
                    {isManage && pendingSubCount > 0 && <span className="chip chip-orange" data-testid="register-pending-count">{pendingSubCount} sub{pendingSubCount === 1 ? "" : "s"} awaiting approval</span>}
                </div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Person</th><th>Type</th><th>Project</th><th>In</th><th>Out</th><th className="numeric">Hours</th><th className="numeric">Cost</th><th>Work</th><th>Approval</th></tr></thead>
                        <tbody>
                            {closed.length === 0 ? (
                                <tr><td colSpan={9} className="text-center py-8 text-[var(--rom-text-3)]" data-testid="register-history-empty">No completed clock events yet.</td></tr>
                            ) : closed.map((e) => {
                                const isSub = e.person_type === "subcontractor";
                                const cfg = isSub ? (APPROVAL_CFG[e.approval_status] || APPROVAL_CFG.pending) : null;
                                return (
                                    <tr key={e.id} data-testid={`history-event-${e.id}`}>
                                        <td className="font-semibold">{e.person_name}</td>
                                        <td><span className={`chip ${e.person_type === "employee" ? "chip-navy" : "chip-orange"}`}>{e.person_type}</span></td>
                                        <td className="text-[var(--rom-text-2)] text-xs">{e.project_name}</td>
                                        <td className="font-mono text-xs">{new Date(e.clock_in).toLocaleString()}</td>
                                        <td className="font-mono text-xs">{e.clock_out ? new Date(e.clock_out).toLocaleString() : "—"}</td>
                                        <td className="numeric font-semibold">{e.hours ?? hoursBetween(e.clock_in, e.clock_out) ?? "—"}h</td>
                                        <td className="numeric font-mono">{formatCurrency(e.cost_amount)}</td>
                                        <td className="text-[var(--rom-text-2)] text-xs max-w-[200px] truncate" title={`${e.work_description || ""} ${e.notes || ""}`}>{e.work_description || "—"}{e.notes ? ` · ${e.notes}` : ""}</td>
                                        <td>
                                            {!isSub ? (
                                                <span className="text-xs text-[var(--rom-text-3)]">via Timesheets</span>
                                            ) : (
                                                <div className="flex items-center gap-2" data-testid={`approval-cell-${e.id}`}>
                                                    <span className={`chip ${cfg.chip}`}>{cfg.label}</span>
                                                    {isManage && e.approval_status !== "approved" && (
                                                        <button onClick={() => decide(e.id, "approved")} disabled={acting === e.id} data-testid={`approve-btn-${e.id}`} className="text-green-600 hover:bg-green-50 p-1 rounded" title="Approve">
                                                            {acting === e.id ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
                                                        </button>
                                                    )}
                                                    {isManage && e.approval_status !== "rejected" && (
                                                        <button onClick={() => decide(e.id, "rejected")} disabled={acting === e.id} data-testid={`reject-btn-${e.id}`} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Reject">
                                                            <XCircle size={14} weight="bold" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
